import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, HeartHandshake, Moon, Sparkles, UserRound } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { trackClick } from "@/lib/track";
import { fadeUp, hoverLift, revealProps, stagger } from "@/lib/motion";

const ITEMS = [
  {
    icon: UserRound,
    title: "С именем ребёнка",
    desc: "Ваш ребёнок становится главным героем: своё имя, свой характер, свои друзья рядом.",
    testId: LANDING.analyzeCardName,
    span: "md:col-span-4",
    accent: true,
  },
  {
    icon: Sparkles,
    title: "Про то, что волнует",
    desc: "Садик, темнота, зубы, переезд, новый братик — сказка мягко проговаривает то, что тревожит",
    testId: LANDING.analyzeCardBirthdate,
    span: "md:col-span-2",
  },
  {
    icon: HeartHandshake,
    title: "Для мамы",
    desc: "Короткая тёплая сказка-поддержка на пять минут тишины — когда хочется просто выдохнуть.",
    testId: LANDING.analyzeCardTarot,
    span: "md:col-span-2",
  },
  {
    icon: Moon,
    title: "Колыбельная",
    desc: "Тихая песня на ночь с озвучкой — можно включить, выключить свет и не держать телефон в руках.",
    testId: LANDING.analyzeCardPalm,
    span: "md:col-span-4",
  },
];

const Features = () => {
  return (
    <section id="features" className="relative overflow-hidden bg-night-900 py-16 md:py-24">
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div {...revealProps} variants={stagger(0.08)} className="mb-12 text-center">
          <motion.p variants={fadeUp} className="mb-3 text-[11px] uppercase tracking-[0.22em] text-amber">
            Сказка для вашего ребёнка
          </motion.p>
          <motion.h2 variants={fadeUp} className="mb-4 font-serif text-3xl text-cream sm:text-4xl lg:text-5xl">
            Какую сказку сделать
          </motion.h2>
          <motion.p variants={fadeUp} className="mx-auto max-w-xl leading-relaxed text-muted-foreground">
            Расскажите про ребёнка и тему — через пару минут получите историю с озвучкой
            и картинкой
          </motion.p>
        </motion.div>

        <motion.div
          {...revealProps}
          variants={stagger(0.09)}
          className="grid gap-4 md:grid-cols-6 md:gap-5"
        >
          {ITEMS.map(({ icon: Icon, title, desc, testId, span, accent }) => (
            <motion.article
              key={title}
              variants={fadeUp}
              whileHover={hoverLift}
              data-testid={testId}
              className={`group relative overflow-hidden rounded-3xl border border-line p-6 backdrop-blur-xl transition-colors duration-300 hover:border-amber/40 md:p-7 ${span} ${
                accent ? "bg-gradient-to-br from-amber/[0.12] via-white/[0.04] to-transparent" : "bg-white/[0.04]"
              }`}
            >
              <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-amber/10 blur-2xl transition-opacity duration-500 group-hover:opacity-100 md:opacity-0" />
              <div className="relative flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-amber/25 bg-amber/10 text-amber">
                  <Icon className="h-6 w-6" />
                </span>
                <div className="min-w-0">
                  <h3 className="mb-1.5 font-serif text-xl text-cream sm:text-2xl">{title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{desc}</p>
                </div>
              </div>
            </motion.article>
          ))}
        </motion.div>

        <motion.div {...revealProps} variants={fadeUp} className="mt-10 flex justify-center">
          <Link
            data-testid={LANDING.analyzeCta}
            to="/chat"
            onClick={() => trackClick("chat")}
            className="inline-flex min-h-[3.25rem] items-center justify-center gap-2 rounded-full bg-amber px-7 py-3.5 font-semibold text-night-900 shadow-glow transition-all duration-300 hover:bg-amber-hover hover:shadow-glow-lg"
          >
            Сделать сказку бесплатно
            <ArrowRight className="h-4 w-4 shrink-0" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
